'use strict'
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useHistory } from 'react-router-dom'
import RoutingBreadcrumbs from '../RoutingBreadcrumbs'
import {
  Box,
  Snackbar,
  CircularProgress,
  Typography,
  Button
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import RefreshIcon from '@material-ui/icons/Refresh'
import PublishIcon from '@material-ui/icons/Publish'
import TuneIcon from '@material-ui/icons/Tune'
import { TerritoryProvider, useTerritory } from './TerritoryContext'
import SidebarNav from './SidebarNav'
import PropertiesTab from './PropertiesTab'
import CompareTab from './CompareTab'
import AnchorsTab from './AnchorsTab'
import MapTab from './MapTab'
import RoutesTab from './RoutesTab'
import RefineRouteTab from './RefineRouteTab'
import CreateScenarioModal from './CreateScenarioModal'
import StalenessModal from './StalenessModal'
import PublishConfirmModal from './PublishConfirmModal'
import CutCrewModal from './CutCrewModal'
import CrewEfficiencyEditor from './CrewEfficiencyEditor'
import AspireDeltaModal from './AspireDeltaModal'

const TAB_LABELS = { properties: 'Properties', map: 'Map', compare: 'Compare', anchors: 'Anchors', refine: 'Refine Route', routes: 'Routes' }

function TerritoryWorkbenchInner () {
  const history = useHistory()
  const shellRef = useRef(null)
  const ctx = useTerritory()

  const {
    url,
    scenarios,
    selectedScenarioId,
    setSelectedScenarioId,
    selectedScenario,
    isScenarioEditable,
    loadingScenarios,
    error,
    crews,
    routes,
    crewIds,
    routeIds,
    propertyCounts,
    setPropertyCounts,
    snackbar,
    showSnackbar,
    closeSnackbar,
    fetchScenarios,
    fetchCrews,
    refreshAll
  } = ctx

  // ── Tab state ─────────────────────────────────────────────
  const [currentTab, setCurrentTab] = useState('properties')

  // ── Modal state (local) ───────────────────────────────────
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [stalenessModalOpen, setStalenessModalOpen] = useState(false)
  const [publishModalOpen, setPublishModalOpen] = useState(false)
  const [publishingScenario, setPublishingScenario] = useState(false)
  const [publishDiffData, setPublishDiffData] = useState(null)
  const [cutCrewModalOpen, setCutCrewModalOpen] = useState(false)
  const [crewEfficiencyOpen, setCrewEfficiencyOpen] = useState(false)
  const [aspireDeltaOpen, setAspireDeltaOpen] = useState(false)
  const [refineRequest, setRefineRequest] = useState(null) // { key: 'route:<id>', ts }
  const [mapReviewRequest, setMapReviewRequest] = useState(null)
  const [staleFreshness, setStaleFreshness] = useState(null)
  const [pendingCreatePayload, setPendingCreatePayload] = useState(null)
  const [syncingFreshness, setSyncingFreshness] = useState(false)
  const syncCancelledRef = useRef(false)
  const mountedRef = useRef(true)
  useEffect(() => () => { mountedRef.current = false }, [])

  useLayoutEffect(() => {
    const updateShellHeight = () => {
      const node = shellRef.current
      if (!node) return
      const top = node.getBoundingClientRect().top
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight
      const bottomInset = 0
      const nextHeight = Math.max(360, Math.floor(viewportHeight - top - bottomInset))
      node.style.setProperty('--territory-workbench-height', `${nextHeight}px`)
    }

    updateShellHeight()
    window.addEventListener('resize', updateShellHeight)
    window.visualViewport?.addEventListener('resize', updateShellHeight)
    return () => {
      window.removeEventListener('resize', updateShellHeight)
      window.visualViewport?.removeEventListener('resize', updateShellHeight)
    }
  }, [])

  // Check staleness when a scenario is selected
  useEffect(() => {
    if (!selectedScenarioId) return
    let cancelled = false
    ;(async () => {
      try {
        const response = await axios.get(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/freshness`)
        if (cancelled) return
        const freshness = response.data
        setStaleFreshness(freshness)
        if (freshness && freshness.isStale) {
          setStalenessModalOpen(true)
        }
      } catch (_err) {
        // silently ignored
      }
    })()
    return () => { cancelled = true }
  }, [selectedScenarioId, url])

  const downloadChangePackage = useCallback((changePackage) => {
    if (!changePackage?.csv) return false
    const blob = new Blob([changePackage.csv], { type: changePackage.contentType || 'text/csv;charset=utf-8;' })
    const objectUrl = window.URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = changePackage.fileName || `territory_publish_changes_${Date.now()}.csv`
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    window.URL.revokeObjectURL(objectUrl)
    return true
  }, [])

  // Create scenario flow
  const handleCreateSubmit = async (payload) => {
    setCreateModalOpen(false)
    const basePayload = {
      name: payload.name,
      sourceScenarioId: payload.sourceScenarioId,
      horizonDays: payload.horizonDays,
      seedMode: payload.seedMode
    }

    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios`, basePayload)
      const scenario = response.data && response.data.scenario
      const createdId = scenario && scenario._id
      await fetchScenarios(createdId)
      if (scenario && scenario.isStaleAtCreation) {
        showSnackbar('Draft scenario created (routing data is stale — consider syncing)', 'warning')
      } else {
        showSnackbar('Draft scenario created', 'success')
      }
    } catch (err) {
      var msg = 'Failed to create scenario'
      try { msg = err.response.data.message || msg } catch (e) {}
      showSnackbar(msg, 'error')
    }
  }

  const handleSyncNow = async () => {
    // Keep modal open — it will show the syncing state
    syncCancelledRef.current = false
    setSyncingFreshness(true)
    showSnackbar('Full Aspire sync started — this can take several minutes.', 'info')

    // Fire the POST but do NOT await it as the gate — a long-lived connection may drop
    // (proxy timeout, sleep, network blip) even though the sync keeps running server-side.
    // Polling below is the sole source of truth for completion.
    axios.post(`${url}/api/aspire/routing/trigger-sync`, { action: 'daily-sync-then-recompute' })
      .then(resp => {
        if (resp.data?.skipped && mountedRef.current) {
          showSnackbar('A sync is already running — waiting for it to finish…', 'info')
        }
      })
      .catch(() => { /* swallow — the connection may drop on a long sync; polling decides truth */ })

    // Poll freshness on its own timer — 48 attempts × 15 s ≈ 12 min total, enough to
    // outlast the real sync regardless of whether the POST connection survives.
    const MAX_ATTEMPTS = 48
    const INTERVAL_MS = 15000
    let cleared = false
    try {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        if (syncCancelledRef.current) break

        try {
          const freshnessResponse = await axios.get(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/freshness`)
          if (syncCancelledRef.current) break
          const data = freshnessResponse.data
          if (mountedRef.current) setStaleFreshness(data)
          if (data && !data.isStale) {
            cleared = true
            if (mountedRef.current) {
              setStalenessModalOpen(false)
              showSnackbar('Data refreshed — proceeding with fresh data.', 'success')
            }
            break
          }
        } catch (_pollErr) {
          // transient freshness-GET failure — keep looping, don't abort
        }

        // Sleep between attempts, but skip the sleep after the last one
        if (attempt < MAX_ATTEMPTS - 1 && !syncCancelledRef.current) {
          await new Promise(r => setTimeout(r, INTERVAL_MS))
        }
      }

      if (!cleared && !syncCancelledRef.current && mountedRef.current) {
        showSnackbar('Sync is still running — reopen the scenario shortly to check.', 'warning')
      }
    } finally {
      if (mountedRef.current) setSyncingFreshness(false)
    }
  }

  const handleProceedStale = async () => {
    setStalenessModalOpen(false)
    if (!pendingCreatePayload) return
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios`, {
        ...pendingCreatePayload,
        proceedWithStaleData: true
      })
      const createdId = response.data?.scenario?._id
      await fetchScenarios(createdId)
      showSnackbar('Draft scenario created (stale data acknowledged)', 'success')
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || 'Failed to create scenario', 'error')
    }
  }

  // Publish flow
  const handlePublishClick = async () => {
    if (!selectedScenarioId || !isScenarioEditable) return
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/diff`, {
        params: { includeMetricsDelta: true }
      })
      setPublishDiffData(response.data || null)
      setPublishModalOpen(true)
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || 'Failed to load publish preview', 'error')
    }
  }

  const handlePublishConfirm = async () => {
    if (!selectedScenarioId) return
    setPublishingScenario(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/publish`)
      const changePackage = response.data?.changePackage || null
      const downloaded = downloadChangePackage(changePackage)
      await fetchScenarios(selectedScenarioId)
      setPublishModalOpen(false)
      showSnackbar(
        downloaded
          ? `Scenario published and CSV downloaded (${changePackage.rowCount || 0} changes)`
          : 'Scenario published to live data',
        'success'
      )
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || 'Failed to publish scenario', 'error')
    } finally {
      setPublishingScenario(false)
    }
  }

  const handleDeleteScenario = async () => {
    if (!selectedScenarioId) return
    const name = selectedScenario?.name || selectedScenarioId
    if (!window.confirm(`Delete scenario "${name}"? This cannot be undone.`)) return
    try {
      await axios.delete(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}`)
      showSnackbar(`Scenario "${name}" deleted`, 'success')
      await fetchScenarios()
    } catch (err) {
      const data = err && err.response && err.response.data
      showSnackbar((data && data.message) || (err && err.message) || 'Failed to delete scenario', 'error')
    }
  }

  if (loadingScenarios) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
        <CircularProgress style={{ color: 'var(--df-primary)' }} />
      </div>
    )
  }

  if (error) {
    return (
      <Box p={3} style={{ background: 'var(--df-surface)', borderRadius: 12 }}>
        <Typography style={{ color: 'var(--df-danger)' }}>Error: {error}</Typography>
        <Box mt={2}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshAll}>Retry</Button>
        </Box>
      </Box>
    )
  }

  return (
    <div ref={shellRef} className="territory-workbench-shell" style={{
      animation: 'df-scaleIn .25s ease'
    }}>
      <SidebarNav
        scenarios={scenarios}
        selectedScenario={selectedScenario}
        selectedScenarioId={selectedScenarioId}
        onScenarioChange={setSelectedScenarioId}
        onBack={() => history.push('/routing/territory/dashboard')}
        currentTab={currentTab}
        onTabChange={setCurrentTab}
        onCutCrew={() => setCutCrewModalOpen(true)}
        onDelete={handleDeleteScenario}
        cutCrewDisabled={!selectedScenarioId || !isScenarioEditable || routes.length === 0}
        staleFreshness={staleFreshness?.isStale}
        anchorBadge={propertyCounts.anchors}
      />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Thin topbar */}
        <div style={{
          height: 50, background: 'var(--df-surface)', borderBottom: '1px solid var(--df-line)',
          display: 'flex', alignItems: 'center', padding: '0 20px', flexShrink: 0
        }}>
          <div style={{ marginTop: 14 }}>
            <RoutingBreadcrumbs items={[
              { label: 'Scenarios', path: '/routing/territory/dashboard' },
              { label: selectedScenario?.name || 'Workbench' }
            ]} />
          </div>
          <div style={{ width: 1, height: 18, background: 'var(--df-line)', margin: '0 12px 2px' }} />
          <span style={{ fontSize: 12, color: 'var(--df-ink-3)', marginBottom: 2 }}>{TAB_LABELS[currentTab]}</span>
          <div style={{ flex: 1 }} />
          {selectedScenarioId && (
            <button
              className="btn btn-ghost"
              style={{ fontSize: 12, padding: '6px 12px', marginRight: 8 }}
              onClick={() => setAspireDeltaOpen(true)}
              title="Show Aspire visits that don't match this scenario yet"
            >
              Aspire Delta
            </button>
          )}
          {isScenarioEditable && (
            <button
              className="btn btn-primary"
              style={{ fontSize: 12, padding: '6px 14px' }}
              onClick={handlePublishClick}
              disabled={publishingScenario}
            >
              <PublishIcon style={{ fontSize: 14 }} />
              Publish Draft
            </button>
          )}
          {selectedScenario && !isScenarioEditable && (
            <span style={{
              fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
              color: 'var(--df-primary)', letterSpacing: '0.05em'
            }}>
              {selectedScenario.status}
            </span>
          )}
          <button
            className="btn btn-ghost"
            style={{ padding: 6 }}
            onClick={() => setCrewEfficiencyOpen(true)}
            title="Edit crew efficiency curve"
          >
            <TuneIcon style={{ fontSize: 16 }} />
          </button>
          <button className="btn btn-ghost" style={{ padding: 6 }} onClick={refreshAll}>
            <RefreshIcon style={{ fontSize: 16 }} />
          </button>
        </div>

        {/* Tab content */}
        <div style={{
          flex: 1,
          minHeight: 0,
          overflow: currentTab === 'map' ? 'hidden' : 'auto',
          position: 'relative'
        }}>
          {currentTab === 'properties' && (
            <PropertiesTab
              url={url}
              selectedScenarioId={selectedScenarioId}
              isScenarioEditable={isScenarioEditable}
              crews={crews}
              crewIds={crewIds}
              routes={routes}
              routeIds={routeIds}
              onSnackbar={showSnackbar}
              onCountsChange={setPropertyCounts}
            />
          )}
          {currentTab === 'compare' && (
            <CompareTab
              url={url}
              selectedScenarioId={selectedScenarioId}
              scenarios={scenarios}
              crews={crews}
              crewIds={crewIds}
              routes={routes}
              routeIds={routeIds}
              onSnackbar={showSnackbar}
            />
          )}
          {currentTab === 'anchors' && (
            <AnchorsTab
              url={url}
              selectedScenarioId={selectedScenarioId}
              crews={crews}
              crewIds={crewIds}
              routes={routes}
              routeIds={routeIds}
              onSnackbar={showSnackbar}
            />
          )}
          {currentTab === 'map' && (
            <MapTab
              url={url}
              selectedScenarioId={selectedScenarioId}
              crews={crews}
              crewIds={crewIds}
              routes={routes}
              routeIds={routeIds}
              onSnackbar={showSnackbar}
              reviewRequest={mapReviewRequest}
              onOpenRefine={(routeId) => {
                setRefineRequest({ key: routeId ? `route:${routeId}` : null, ts: Date.now() })
                setCurrentTab('refine')
              }}
            />
          )}
          {currentTab === 'routes' && (
            <RoutesTab
              url={url}
              onSnackbar={showSnackbar}
              onRoutesChanged={() => { if (selectedScenarioId) fetchCrews(selectedScenarioId) }}
            />
          )}
          {currentTab === 'refine' && (
            <RefineRouteTab
              url={url}
              selectedScenarioId={selectedScenarioId}
              isScenarioEditable={isScenarioEditable}
              onSnackbar={showSnackbar}
              requestedCrewRequest={refineRequest}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <CreateScenarioModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        scenarios={scenarios}
        selectedScenarioId={selectedScenarioId}
        onSubmit={handleCreateSubmit}
      />
      <StalenessModal
        open={stalenessModalOpen}
        onClose={() => {
          syncCancelledRef.current = true
          setStalenessModalOpen(false)
        }}
        freshness={staleFreshness}
        onSyncNow={handleSyncNow}
        onProceedStale={handleProceedStale}
        syncing={syncingFreshness}
      />
      <PublishConfirmModal
        open={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        onConfirm={handlePublishConfirm}
        scenarioName={selectedScenario?.name}
        diffData={publishDiffData}
        publishing={publishingScenario}
      />
      <AspireDeltaModal
        open={aspireDeltaOpen}
        onClose={() => setAspireDeltaOpen(false)}
        url={url}
        scenarioId={selectedScenarioId}
        onSnackbar={showSnackbar}
      />

      <CutCrewModal
        open={cutCrewModalOpen}
        onClose={() => setCutCrewModalOpen(false)}
        url={url}
        scenarioId={selectedScenarioId}
        routes={routes}
        onApplied={(message, result) => {
          fetchScenarios(selectedScenarioId)
          fetchCrews(selectedScenarioId)
          setCurrentTab('map')
          setMapReviewRequest({
            type: 'cut_crew_unassigned',
            token: Date.now(),
            cutRoute: result?.cutRoute || null,
            summary: result?.summary || null
          })
          showSnackbar(message, 'success')
        }}
      />

      <CrewEfficiencyEditor
        open={crewEfficiencyOpen}
        onClose={() => setCrewEfficiencyOpen(false)}
        url={url}
        onSaved={(message) => showSnackbar(message, 'success')}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

function TerritoryWorkbench ({ url }) {
  return (
    <TerritoryProvider url={url}>
      <TerritoryWorkbenchInner />
    </TerritoryProvider>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(TerritoryWorkbench)
